import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { DateTime } from 'luxon';
import { of } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';
import { ConfirmationPageActions } from '../../create-request/actions';
import { FormatPercentPipe } from '../../shared/pipes/FormatPercentPipe';
import { Constants, navRouteConstants } from '../../shared/utilities/Constants';
import { AccountInfoActions } from '../actions';
import { CashType } from '../models';
import { AccountInfoResponse } from '../models/AccountInfo';
import { AccountInfoApiResponse } from '../models/AccountInfoApiResponse';
import { AccountInfoHeaderService } from '../services/account-info.service';
import { StandingAuthConfirmationPageActions } from '../../standing-authorizations/actions';
import { isDevMode } from '@angular/core';
import { SharedActions } from '../../shared/actions';
import { CoreService } from '@charlesschwab/sac-ui-core';
import { ContextService } from 'projects/integration/src/app/context.service';
import { ValidateWireFlagsService } from '../../shared/services/validate-WireFlags.service';
import { isPalAccountFlow } from '../../shared/utilities/AccountFlow';
import { PALHeaderResponse } from '../models/PALHeaderResponse';

@Injectable()
export class AccountInfoEffects {

  loadAccountInfo$ = createEffect(() =>
    this.actions$.pipe(ofType(
        AccountInfoActions.updateSubAccount,
        ConfirmationPageActions.createAnotherRequestClicked,
        ConfirmationPageActions.viewTransactionStatusClicked,
        StandingAuthConfirmationPageActions.createNewStandingAuthClicked,
        StandingAuthConfirmationPageActions.returnStandingAuthClicked,
        AccountInfoActions.refresh),
      exhaustMap((action) => {
        const accountId = action.accountId?.trim();
        if (isValidAccountId(accountId)) {
          if (isPalAccountFlow(this.coreService.currentContext.contextType, this.validateWireFlagsService.enablePalWireCreateTransaction())) {
            return this.accountInfoService.getAccountInfoForPal(accountId).pipe(
              map((accountInfo: PALHeaderResponse) => {
                return AccountInfoActions.loadPalAccountInfoSuccess(accountInfo);
              }),
              catchError(async (err) => {
                if (isDevMode())
                {
                  return SharedActions.displayFilePickerModal({content: 'Account Info'});
  
                }
                return AccountInfoActions.loadAccountInfoServiceFailure
                (
                  {
                    errorMsg: err.message
                  }
                )
              }
              )
            );
          } else {
            return this.accountInfoService.getAccountInfo(accountId).pipe(
              map((accountInfo: AccountInfoApiResponse) => {
                const accountInfoResponse = mapApiResponsetoAccountInfo(accountInfo);
                return AccountInfoActions.loadAccountInfoSuccess(accountInfoResponse);
              }),
              catchError(async (err) => {
                if (isDevMode())
                {
                  return SharedActions.displayFilePickerModal({content: 'Account Info'});
  
                }
                return AccountInfoActions.loadAccountInfoServiceFailure
                (
                  {
                    errorMsg: err.message
                  }
                )
              }
              )
            );
          }
        } else {
          return of(
            AccountInfoActions.loadAccountInfoFailure({
              errorMsg: 'Invalid Account number entered'
            })
          );
        }
      })
    )
  );

  constructor(
    private actions$: Actions,
    private accountInfoService: AccountInfoHeaderService,
    private router: Router,
    private contextService: ContextService,
    private coreService: CoreService,
    private validateWireFlagsService: ValidateWireFlagsService
  ) { }
}

export function isValidAccountId(accountId?: string): boolean {
  const accountIdLength = accountId?.trim().length;
  return accountIdLength === Constants.minAccountNumberLength
    || accountIdLength === Constants.maxAccountNumberLength
    || accountIdLength === Constants.palAccountNumberLength;
}

export function mapApiResponsetoAccountInfo(accountInfo: AccountInfoApiResponse): AccountInfoResponse {
  const formatPercent = new FormatPercentPipe;
  const isRmdEligible = accountInfo.headerInfoDataResponse.isRmdEligible;
  return {
    accountId: accountInfo.headerInfoDataResponse?.accountTitle.subAccountNumber,
    categoryType: 'ACCOUNT',
    subAccountNameLine1: accountInfo.headerInfoDataResponse?.accountTitle.line1,
    subAccountNameLine2: accountInfo.headerInfoDataResponse?.accountTitle.line2,
    subAccountNameLine3: accountInfo.headerInfoDataResponse?.accountTitle.line3,
    registrationType: accountInfo.headerInfoDataResponse?.accountTitle.regShortName,
    formerTdaIndicator: accountInfo.headerInfoDataResponse?.formerTdaIndicator,
    cashBalances: {
      availableCash: {
        type: CashType.Available,
        amount: +accountInfo.headerInfoDataResponse?.cashBalances.availableCash,
      },
      unsettledCash: {
        type: CashType.Unsettled,
        amount: +accountInfo.headerInfoDataResponse?.cashBalances.unsettledCash,
      },
      marketValue: {
        type: CashType.MarketValue,
        amount: +accountInfo.headerInfoDataResponse?.cashBalances.marketValue,
      },
      marginAvailable: {
        type: CashType.Margin,
        amount: +accountInfo.headerInfoDataResponse?.cashBalances.marginAvailable,
      },
      rmdCurrentYear: {
        type: CashType.RmdCurrent,
        amount: isRmdEligible ? +accountInfo.headerInfoDataResponse?.cashBalances.rmdCurrentYear : 'N/A',
      },
      rmdYtd: {
        type: CashType.RmdYtd,
        amount: +accountInfo.headerInfoDataResponse?.cashBalances.rmdYtd,
      },
      rmdRemaining: {
        type: CashType.RmdRemaining,
        amount: isRmdEligible ? +accountInfo.headerInfoDataResponse?.cashBalances.rmdRemaining : 'N/A',
      },
      trustPrincipalBalance: {
        type: CashType.trustPrincipalBalance,
        amount: +accountInfo.pIBalanceResponse?.principalBalance,
      },
      trustIncomeBalance: {
        type: CashType.trustIncomeBalance,
        amount: +accountInfo.pIBalanceResponse?.incomeBalance,
      },
      contributionYtd: {
        type: CashType.contributionYtd,
        amount: +accountInfo.headerInfoDataResponse?.cashBalances.contributionYtd,
      },
      // PAL balance fields
      currentBalance: accountInfo.headerInfoDataResponse?.cashBalances.currentBalance ? {
        type: CashType.OutstandingLoans,
        amount: +accountInfo.headerInfoDataResponse.cashBalances.currentBalance,
      } : undefined,
      availableCredit: accountInfo.headerInfoDataResponse?.cashBalances.availableCredit ? {
        type: CashType.AvailableCredit,
        amount: +accountInfo.headerInfoDataResponse.cashBalances.availableCredit,
      } : undefined,
      displayCredit: accountInfo.headerInfoDataResponse?.cashBalances.displayCredit ? {
        type: CashType.CreditLine,
        amount: +accountInfo.headerInfoDataResponse.cashBalances.displayCredit,
      } : undefined,
      billingTotalChargesDue: accountInfo.headerInfoDataResponse?.cashBalances.billingTotalChargesDue ? {
        type: CashType.FinanceCharges,
        amount: +accountInfo.headerInfoDataResponse.cashBalances.billingTotalChargesDue,
      } : undefined,
      billingTotalPaymentDue: accountInfo.headerInfoDataResponse?.cashBalances.billingTotalPaymentDue ? {
        type: CashType.TotalPaymentDue,
        amount: +accountInfo.headerInfoDataResponse.cashBalances.billingTotalPaymentDue,
      } : undefined,
      billingPaymentDueDt: accountInfo.headerInfoDataResponse?.cashBalances.billingPaymentDueDt ? {
        type: CashType.PaymentDueDate,
        amount: 'N/A',
} : undefined,
    },
    taxes: {
      taxPayerId: '0',
      withholdFederal: false,
      withholdState: false,
      federalTaxRate: formatPercent.transform(accountInfo.headerInfoDataResponse?.taxInfo?.federalTaxRate),
      stateTaxRate: accountInfo.headerInfoDataResponse?.taxInfo?.stateTaxRate,
      state: accountInfo.headerInfoDataResponse?.taxInfo?.state,
      stateCustomerTaxElections: '',
      fedCustomerTaxElections: '',
      hasStateTax: accountInfo.headerInfoDataResponse?.taxInfo?.hasStateTax ?? false
    },
    classifications: {
      isIra: accountInfo.headerInfoDataResponse?.isIra,
      isPtrs: accountInfo.headerInfoDataResponse?.isTrust,
      isIip: accountInfo.headerInfoDataResponse?.isIip,
      isPreview: accountInfo.headerInfoDataResponse?.isPreview,
      isEmployee: accountInfo.headerInfoDataResponse?.isEmployee,
      isDbsUser: accountInfo.headerInfoDataResponse?.isDbsUser,
      isDemoUser: accountInfo.headerInfoDataResponse?.isDemoUser,
      isFsaUser: accountInfo.headerInfoDataResponse?.isFsaUser,
      isTestamentaryTrust: accountInfo.headerInfoDataResponse?.accountTitle?.regShortName?.toLowerCase() === 'test trust',
      isEstate: accountInfo.headerInfoDataResponse?.accountTitle?.regShortName?.toLowerCase() === 'estate',
      isGuardian: accountInfo.headerInfoDataResponse?.accountTitle?.regShortName?.toLowerCase() === 'guardian',
      isConserv: accountInfo.headerInfoDataResponse?.accountTitle?.regShortName?.toLowerCase() === 'conserv',
      hasBundledOffer: accountInfo.headerInfoDataResponse?.bsloaEnabled,
    },
    eauthorization: {
      areAllAccountHoldersIneligible: accountInfo.eAuthDataResponse?.areAllAccountHoldersIneligible,
      isEauthEligible: accountInfo.eAuthDataResponse?.isEauthEligible,
      numberOfAccountHolders: accountInfo.eAuthDataResponse?.numberOfAccountHolders,
      customers: accountInfo.eAuthDataResponse?.customers,
      isAccountRestricted: accountInfo.eAuthDataResponse?.isAccountRestricted,
      isOrgAccount: accountInfo.eAuthDataResponse?.isOrgAccount,
      restrictionReason: accountInfo.eAuthDataResponse?.restrictionReason
    },
    pfaDetails: {
      hasWpfaRole: accountInfo.headerInfoDataResponse?.pfaDetails?.hasWpfaRole,
      hasLpfaRole: accountInfo.headerInfoDataResponse?.pfaDetails?.hasLpfaRole,
      hasNdfaRole: accountInfo.headerInfoDataResponse?.pfaDetails?.hasNdfaRole,
      hasCustodyWPFARole: accountInfo.headerInfoDataResponse?.pfaDetails?.hasCustodyWPFARole,
      hasBSLOAWPFARole: accountInfo.headerInfoDataResponse?.pfaDetails?.hasBSLOAWPFARole
    },
    updated: DateTime.now().toString(),
    acknowledgementText: '',
    userName: '',
    isBtMasterOnly: false,
    primaryMaster: ''
  };
}
